import { useCallback } from 'react'
import { Link } from 'react-router-dom'

import { BarList } from '@/components/charts/BarList'
import { Funnel } from '@/components/charts/Funnel'
import { StatTile } from '@/components/charts/StatTile'
import { TrendGrid } from '@/components/charts/Trend'
import { Badge, Card, ErrorNote, PageHeader, Spinner } from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { api } from '@/lib/api'
import { matchTypeLabel, percent, thousands } from '@/lib/format'
import type {
  DashboardStats,
  EngineFeedback,
  FunnelStage,
  TimeseriesPoint,
} from '@/types'

export function DashboardPage() {
  const stats = useQuery(
    useCallback(() => api.get<DashboardStats>('/dashboard/stats'), []),
  )
  const funnel = useQuery(
    useCallback(() => api.get<FunnelStage[]>('/dashboard/funnel'), []),
  )
  const series = useQuery(
    useCallback(() => api.get<TimeseriesPoint[]>('/dashboard/timeseries', { days: 30 }), []),
  )
  const feedback = useQuery(
    useCallback(() => api.get<EngineFeedback[]>('/dashboard/engine-feedback'), []),
  )

  if (stats.loading && !stats.data) return <Spinner label="Loading dashboard" />
  if (stats.error) return <ErrorNote message={stats.error} onRetry={stats.reload} />
  if (!stats.data) return null

  const s = stats.data

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="The funnel is the product metric. Conversations are the means, not the goal."
        action={
          <div className="flex flex-wrap items-center gap-2">
            {s.whatsapp_dry_run && (
              <Badge tone="warning" icon="⚠">
                Dry run — nothing is being sent
              </Badge>
            )}
            {!s.ai_enabled ? (
              <Badge tone="warning" icon="⚠">
                AI disabled — deterministic fallbacks
              </Badge>
            ) : (
              <Badge tone="neutral" icon="◆">
                {s.ai_provider}
              </Badge>
            )}
            {s.ai_enabled && !s.semantic_matching && (
              /* Groq serves chat but no embeddings, so this is a normal
                 operating mode rather than a fault - say what it costs. */
              <Badge tone="warning" icon="⚠">
                Lexical matching — no embedding provider
              </Badge>
            )}
          </div>
        }
      />

      {/* The single number the dashboard leads with. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Successful connections"
          value={s.successful_connections}
          hint={`${percent(s.conversion_rate / 100, 1)} of all contacts`}
          emphasis
        />
        <StatTile
          label="Introductions made"
          value={s.introductions_made}
          hint={`${s.approved_matches} matches approved`}
        />
        <StatTile
          label="High-quality matches"
          value={s.high_quality_matches}
          hint={`of ${thousands(s.active_matches)} active`}
        />
        <StatTile
          label="Actionable profiles"
          value={s.profiles_actionable}
          hint={`of ${thousands(s.profiles_started)} started`}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Contacts" value={s.total_contacts} />
        <StatTile
          label="Active conversations"
          value={s.active_conversations}
          hint={`${thousands(s.conversations)} in total`}
        />
        <StatTile label="Open opportunities" value={s.open_opportunities} />
        <StatTile
          label="Opted out"
          value={s.opted_out}
          hint="Permanently excluded from outreach"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card
          className="xl:col-span-2"
          title="Contacts → Connections"
          subtitle="Where people drop out of the journey"
        >
          {funnel.loading && !funnel.data ? (
            <Spinner />
          ) : funnel.error ? (
            <ErrorNote message={funnel.error} onRetry={funnel.reload} />
          ) : (
            <Funnel stages={funnel.data ?? []} />
          )}
        </Card>

        <Card
          title="Match quality by type"
          subtitle="Approved ÷ reviewed, from real admin decisions"
          action={
            <Link to="/opportunities" className="text-xs text-ink-muted hover:text-ink">
              View all →
            </Link>
          }
        >
          {feedback.loading && !feedback.data ? (
            <Spinner />
          ) : (
            <BarList
              items={(feedback.data ?? [])
                .filter((row) => row.total > 0)
                .slice(0, 8)
                .map((row) => ({
                  key: row.match_type,
                  label: matchTypeLabel(row.match_type),
                  value: row.total,
                  detail:
                    row.precision === null
                      ? `${row.total} found · none reviewed yet`
                      : `${percent(row.precision)} approved · avg score ${row.avg_score}`,
                }))}
              emptyLabel="No matches have been computed yet."
            />
          )}
        </Card>
      </div>

      <Card
        className="mt-4"
        title="Last 30 days"
        subtitle="Each metric keeps its own scale — a shared axis would imply a correlation that is not there"
      >
        {series.loading && !series.data ? (
          <Spinner />
        ) : series.error ? (
          <ErrorNote message={series.error} onRetry={series.reload} />
        ) : (
          <TrendGrid points={series.data ?? []} />
        )}
      </Card>
    </>
  )
}
